import { useCallback, useEffect, useRef, useState } from 'react';
import { normalisedUrl, useNormalisedPathname } from 'utilities/router/api-fetch-path.js';
import { getWindowLocation } from 'utilities/window.js';
import { PageChangeContext } from './page-change-context.js';

/**
 * Only updates when page transition has happened NOT on first page change
 * At start state will be 0, then page change state will be either 1 or two.
 * Can check if page has changed wuth useEffect
 */
export const PageChangeProvider = ({ children }: { children: JSX.Element }) => {
  const pathname = useNormalisedPathname();
  const previousPathsRef = useRef<string[]>([]);
  const [pageUrlChanged, setPageUrlChanged] = useState<string>();

  useEffect(() => {
    if (pathname) {
      const lastPreviousPath = previousPathsRef.current[previousPathsRef.current.length - 1];
      if (!lastPreviousPath) {
        /** First time url */
        previousPathsRef.current = [pathname];
      } else {
        if (lastPreviousPath !== pathname) {
          previousPathsRef.current = [...previousPathsRef.current, pathname];
          setPageUrlChanged(pathname);
        }
      }
    }
  }, [pathname]);

  const getCurrentPath = useCallback(() => {
    const path = previousPathsRef.current[previousPathsRef.current.length - 1];
    if (path) {
      return path;
    }
    /** On first load this does not populate in time, so we use window.location */
    const loc = getWindowLocation();
    if (loc) {
      const url = new URL(loc.href);
      return normalisedUrl(url.pathname);
    }
    throw new Error('No path found in PageChangeProvider');
  }, []);

  /** If number is 1 or 2 page has changed */
  return <PageChangeContext.Provider value={{ pageUrlChanged, getCurrentPath }}>{children}</PageChangeContext.Provider>;
};
